import DateFnsUtils from '@date-io/moment';
import { yupResolver } from '@hookform/resolvers/yup';
import { CircularProgress, FormControl, InputLabel, MenuItem } from '@material-ui/core';
import Autocomplete from '@material-ui/lab/Autocomplete';
import { DatePicker, MuiPickersUtilsProvider, TimePicker } from '@material-ui/pickers';
import * as Sentry from '@sentry/react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { DateTime } from 'luxon';
import moment from 'moment';
import React, { useEffect, useState } from 'react';
import { Col, Modal, Row } from 'react-bootstrap';
import {
  Controller,
  FormProvider,
  RegisterOptions,
  Resolver,
  useFieldArray,
  useForm,
  useFormContext,
} from 'react-hook-form';
import { useAsyncResult } from 'react-use-async-result';
import { useDebounce } from 'usehooks-ts';
import * as yup from 'yup';
import { create } from 'zustand';
import { ANALYTICS_EVENTS, track } from '../analytics';
import { foodApi, mealApi } from '../api';
import {
  CreateMealItemRequest,
  CreateMealRequest,
  CreateMealRequestMealNameEnum,
  FoodResponse,
  MealItemCustomAddonRequest,
  MealResponse,
  PatientResponse,
  UpdateMealItemRequest,
} from '../api/generated';
import { useCurrentPatientData, useStore } from '../context';
import { Trans, useTranslation } from '../i18n';
import {
  ActivityFeedItem,
  MACRO_NUTRIENT_QUERY_KEY,
  MacroNutrientSummaryCard,
  useMealCache,
} from '../pages/patient-logV2';
import MealThumbnailImage from '../theme/icons/meal-thumb.png';
import { FormatAMPMStr, formatDate, formatDateLongMonth, formatTime, getNearestMealName } from '../utils/formatDate';
import { maybe, titleCase } from '../utils/formatters';
import { CGMMealGlucoseChart } from './cgm/CGMGlucoseChart';
import { OriginalImage } from './image';
import { itemAddonsToStr, MealItemMacros, MealItemPercentEaten } from './mealCard';
import { MuiCustomSelect, MuiCustomTextField } from './muiCustomInput';
import { PatientSurvey } from './patientSurvey';
import './showMealV2.scss';
import { useMealCache as globalUseMealCache } from './mealCache';

function textJoiner(idx: number, length: number) {
  if (idx === length - 1) {
    return '';
  }
  return ', ';
}

export const AutoScrollHere = () => {
  const scrollRef = React.useRef<HTMLDivElement>(null);
  useEffect(() => {
    scrollRef.current?.scrollIntoView({
      behavior: 'smooth',
      block: 'center',
    });
  }, []);
  return <div ref={scrollRef} />;
};
export const useRecentlyModifiedMeal = create(() => ({
  recentlyModifiedMeal: null as MealResponse | null,
}));

const ItemOrAddonEditRow = (props: {
  itemOrAddon: CreateMealItemRequest | MealItemCustomAddonRequest,
  type: string,
  onChange: (fields: Partial<CreateMealItemRequest | MealItemCustomAddonRequest>) => void,
  onRemove: () => void,
  itemIndex?: number,
  addonIndex?: number,
  rowStyle?: React.CSSProperties,
}) => {
  const { itemOrAddon, type, itemIndex, addonIndex } = props;
  const { patient } = useStore();
  const { t, tAttr } = useTranslation();
  const { register, formState: { errors, isSubmitted } } = useFormContext();

  const foodQuery = useQuery(['food', itemOrAddon.food_name], async () => {
    const res = await foodApi.appApiFoodFoodSearchGetFoodQuery({
      food_name: itemOrAddon.food_name,
    });
    return res.data;
  });

  const [foodSearchTerm, setFoodSearchTerm] = useState(null as string | null);
  const debounceFoodSearchTerm = useDebounce(foodSearchTerm, 100);
  const foodSearchQuery = useQuery(['foodSearch', debounceFoodSearchTerm], async () => {
    if (!foodSearchTerm) {
      return [];
    }
    const res = await foodApi.appApiFoodFoodSearchSearchByText({
      search_text: foodSearchTerm,
      context_user_id: '' + patient.patient_id,
    });
    return res.data.results;
  }, {
    enabled: foodSearchTerm == debounceFoodSearchTerm,
  });

  const inputRef = React.useRef<HTMLInputElement>(null);

  const controlRegister = type === 'meal_items'
    ? `${type}.${itemIndex}`
    // eslint-disable-next-line i18next/no-literal-string
    : `meal_items.${itemIndex}.custom_addons.${addonIndex}`;

  return (
    <Row style={{ display: 'flex', gap: 10, paddingLeft: 15, marginTop: 0, alignItems: 'flex-end' }}>
      <div style={{ flex: 1, marginTop: 10 }}>
        <Autocomplete
          loading={foodSearchQuery.isLoading}
          options={(foodSearchQuery.data || []).map(f => {
            const food_name = tAttr(f as FoodResponse & { name_translations: object | null }, 'name');
            return food_name;
          })}
          onChange={(event, value) => {
            props.onChange({ food_name: value });
          }}
          onBlur={(event) => {
            // foodNameRegister.onBlur(event);
          }}
          value={itemOrAddon.food_name}
          onInputChange={(event, value) => {
            setFoodSearchTerm(value);
          }}
          renderInput={(params) => (
            <MuiCustomTextField
              {...params}
              ref={inputRef}
              {...register(`${controlRegister}.food_name`)}
              label={t('Food name')}
              variant="outlined"
              InputProps={{
                ...params.InputProps,
                endAdornment: (
                  <>
                    {foodSearchQuery.isLoading ? <CircularProgress color="inherit" size={20} /> : null}
                    {params.InputProps.endAdornment}
                  </>
                ),
              }}
            />
          )}
        />
        {errors.meal_items?.[itemIndex]?.food_name && isSubmitted && type === 'meal_items'
          && <p className="text-danger">{errors?.meal_items[itemIndex]?.food_name?.message}</p>}
        {errors.meal_items?.[itemIndex]?.custom_addons && isSubmitted && type === 'custom_addons'
          && (
            <p className="text-danger">
              {errors?.meal_items[itemIndex]?.custom_addons[addonIndex]?.food_name?.message}
            </p>
          )}
      </div>
      <div style={{ flex: '0 0 75px', width: 75, marginTop: -20 }}>
        <MuiCustomTextField
          variant="outlined"
          label={t('Count')}
          type="number"
          {...register(`${controlRegister}.servings`, { valueAsNumber: true })}
          value={itemOrAddon.servings}
          onChange={(event) => {
            props.onChange({
              servings: +event.target.value < 0 ? 0 : +event.target.value,
            });
          }}
        />
        {errors.meal_items?.[itemIndex]?.servings && isSubmitted && type === 'meal_items'
          && <p className="text-danger">{errors?.meal_items[itemIndex]?.servings?.message}</p>}
        {errors.meal_items?.[itemIndex]?.custom_addons && isSubmitted && type === 'custom_addons'
          && (
            <p className="text-danger">
              {errors?.meal_items[itemIndex]?.custom_addons[addonIndex]?.servings?.message}
            </p>
          )}
      </div>
      <div style={{ flex: 0.5, marginTop: -20 }}>
        <FormControl variant="outlined" className="meal-unit-input">
          <InputLabel id="serving_unit_label">
            <Trans>Unit</Trans>
          </InputLabel>
          <MuiCustomSelect
            id="serving_unit_label"
            value={foodQuery.data?.serving_units?.some(su => su.label === itemOrAddon.serving_unit_label)
              ? itemOrAddon.serving_unit_label
              : ''}
            variant="outlined"
            style={{ height: '56px' }}
            label={t('Unit')}
            {...register(`${controlRegister}.serving_unit_label`)}
            disabled={!foodQuery.data?.serving_units || foodQuery.data.serving_units.length === 0}
            onChange={(event) => {
              const servingUnitAmount = foodQuery.data?.serving_units
                ?.find(su => su.label === event.target.value)
                ?.amount;
              props.onChange({
                serving_unit_label: event.target.value as string,
                serving_unit_amount: servingUnitAmount || 0,
              });
            }}
          >
            {foodQuery.data?.serving_units?.map((su, index) => (
              <MenuItem key={index} value={su.label}>
                {su.label}
              </MenuItem>
            ))}
          </MuiCustomSelect>
        </FormControl>
        {errors.meal_items?.[itemIndex]?.serving_unit_label && isSubmitted && type === 'meal_items'
          && <p className="text-danger">{errors?.meal_items[itemIndex]?.serving_unit_label?.message}</p>}
        {errors.meal_items?.[itemIndex]?.custom_addons && isSubmitted && type === 'custom_addons'
          && (
            <p className="text-danger">
              {errors?.meal_items[itemIndex]?.custom_addons[addonIndex]?.serving_unit_label?.message}
            </p>
          )}
      </div>
      <div style={{ width: 20, display: 'flex', alignSelf: 'center', marginTop: '-5px', marginLeft: '10px' }}>
        <span role="button" style={{ cursor: 'pointer' }} onClick={() => props.onRemove()}>
          <i className="fas fa-trash text-danger" />
        </span>
      </div>
    </Row>
  );
};

const EditMealItemRow = (props: {
  mealItem: CreateMealItemRequest & { food_photo_url?: string },
  register: (name: keyof CreateMealItemRequest, options?: RegisterOptions) => any,
  mealIndex: number,
  onChange: (fields: Partial<CreateMealItemRequest>) => void,
  onRemove: () => void,
}) => {
  const { mealItem, mealIndex } = props;
  const addons = mealItem.custom_addons || [];
  const { t } = useTranslation();
  return (
    <Row className="dx-0 bx-1" style={{ marginLeft: 0, marginRight: 0 }}>
      <Col xs="auto" className="py-3 d-flex" style={{ gap: '20px', paddingLeft: 0, paddingRight: 0 }}>
        <img
          src={mealItem.food_photo_url || MealThumbnailImage}
          width={80}
          height={80}
        />
      </Col>
      <Col className="py-3" style={{ flex: 1 }}>
        <ItemOrAddonEditRow
          itemOrAddon={mealItem}
          type="meal_items"
          itemIndex={mealIndex}
          rowStyle={{ marginBottom: 20 }}
          onChange={(fields) => {
            props.onChange(fields);
          }}
          onRemove={() => props.onRemove()}
        />
        <Row>
          <Col>
            <h5 className="mt-4">
              <span
                role="button"
                className="py-1"
                style={{ cursor: 'pointer' }}
                onClick={() =>
                  props.onChange({
                    'custom_addons': [
                      ...addons,
                      {
                        food_name: '',
                        servings: 1,
                        serving_unit_label: '',
                        serving_unit_amount: 0,
                      },
                    ],
                  })}
              >
                <Trans>Addons</Trans>{'  '}<i className="fas fa-plus mr-1" />
              </span>
            </h5>
          </Col>
        </Row>
        <div className="mt-3" style={{ display: 'flex', gap: 15, flexDirection: 'column' }}>
          {addons.map((addon, index) => (
            <ItemOrAddonEditRow
              key={index}
              itemOrAddon={addon}
              type="custom_addons"
              itemIndex={mealIndex}
              addonIndex={index}
              rowStyle={{ marginBottom: 20 }}
              onChange={(fields) => {
                props.onChange({
                  custom_addons: addons.map(a => {
                    if (a === addon) {
                      return {
                        ...a,
                        ...fields,
                      };
                    }
                    return a;
                  }),
                });
              }}
              onRemove={() => {
                props.onChange({
                  custom_addons: addons.filter(a => a !== addon),
                });
              }}
            />
          ))}
        </div>
        <Row>
          <Col>
            <h5 className="my-4">
              <Trans>Note</Trans>
            </h5>
          </Col>
        </Row>
        <Row>
          <Col style={{ marginTop: -20 }}>
            <MuiCustomTextField
              placeholder={t('Notes...')}
              variant="outlined"
              multiline
              onChange={(event) => {
                props.onChange({ note: event.target.value as string });
              }}
              value={mealItem.note}
            />
          </Col>
        </Row>
      </Col>
    </Row>
  );
};

const DISPLAY_MODE_VIEW = 'view';
const DISPLAY_MODE_EDIT = 'edit';
const DISPLAY_MODE_ADD = 'add';

export const ShowMealV2 = (props: {
  selectedMeal: MealResponse | null,
  initialMode: 'view' | 'edit' | 'add',
  activityData: ActivityFeedItem[],
  onHide: () => void,
  isCopyMode?: boolean,
}) => {
  const { selectedMeal, initialMode, activityData, onHide, isCopyMode } = props;
  const { patient, clinician } = useStore();
  const { i18n, t, tAttr } = useTranslation();
  const [displayMode, setDisplayMode] = useState(initialMode);
  const patientFlags = useCurrentPatientData();
  const queryClient = useQueryClient();
  const saveMealReq = useAsyncResult<MealResponse>();
  const deleteMealReq = useAsyncResult();

  const [mealPhotoShowLarge, setMealPhotoShowLarge] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  const editing = displayMode === DISPLAY_MODE_EDIT || displayMode === DISPLAY_MODE_ADD;

  const getEmptyMealItem = (): CreateMealItemRequest => ({
    food_name: '',
    servings: 1,
    serving_unit_label: '',
    serving_unit_amount: 0,
    addons: [],
    extra_addons: [],
    note: '',
  });

  const getMealForEdit = () => {
    if (selectedMeal) {
      return selectedMeal;
    }

    const now = DateTime.local();

    return {
      meal_name: getNearestMealName(patient as any as PatientResponse, now.toJSDate()),
      meal_date: formatDate(now),
      meal_time: formatTime(now),
      meal_items: [getEmptyMealItem()],
    };
  };

  const formValidationSchema = yup.object({
    meal_date: yup.string().required(),
    meal_time: yup.string().required(),
    meal_name: yup.string().required(),
    meal_items: yup.array().of(
      yup.object().shape({
        food_name: yup.string().required(t('Food name is required')),
        servings: yup.number().required(t('Count is required')),
        serving_unit_label: yup.string().required(t('Unit is required')),
        serving_unit_amount: yup.number().required(),
        custom_addons: yup.array().of(
          yup.object().shape({
            food_name: yup.string().required(t('Food name is required')),
            servings: yup.number().required(t('Count is required')),
            serving_unit_label: yup.string().required(t('Unit is required')),
            serving_unit_amount: yup.number().required(),
          }),
        ),
      }),
    ),
  });

  const form = useForm<CreateMealRequest>({
    reValidateMode: 'onChange',
    defaultValues: {
      meal_date: isCopyMode ? '' : (selectedMeal?.meal_date || DateTime.now().toISODate()),
      meal_time: selectedMeal?.meal_time || DateTime.now().toFormat('HH:mm:ss'),
      meal_name: selectedMeal?.meal_name
        || getNearestMealName(patient as any as PatientResponse, DateTime.now().toJSDate()),
      meal_items: selectedMeal?.meal_items?.map(item => ({
        id: item.id,
        food_name: item.food_name,
        servings: item.servings,
        serving_unit_label: item.serving_unit_label,
        serving_unit_amount: item.serving_unit_amount,
        custom_addons: item.custom_addons?.map(addon => ({
          food_name: addon.food_name,
          servings: addon.servings,
          serving_unit_label: addon.serving_unit_label,
          serving_unit_amount: addon.serving_unit_amount,
        })) || [],
        note: item.note,
      })) || [],
    },
    resolver: yupResolver(formValidationSchema) as Resolver<any>,
  });
  const mealItemsField = useFieldArray({
    control: form.control,
    name: 'meal_items',
    keyName: '_rhf_id',
  });

  const handleHide = () => {
    if (isCopyMode) {
      onHide();
      return;
    }

    switch (displayMode) {
      case DISPLAY_MODE_EDIT:
        setDisplayMode(DISPLAY_MODE_VIEW);
        break;
      case DISPLAY_MODE_ADD:
        if (confirm(t('Are you sure you want to discard this meal?'))) {
          onHide();
        }
        break;
      case DISPLAY_MODE_VIEW:
        onHide();
        break;
    }
  };

  const handleUpdateMeal = async () => {
    try {
      const formValues = form.getValues();

      // 1. Update meal-level attributes
      await mealApi.appApiMealPutMeal({
        patient_id: patient.patient_id,
        meal_id: selectedMeal.id,
        UpdateMealRequest: {
          meal_date: formValues.meal_date,
          meal_time: formValues.meal_time,
          meal_name: formValues.meal_name.toLowerCase() as CreateMealRequestMealNameEnum,
        },
      });

      // 2. Separate items into updates and creates
      const itemsToUpdate = (formValues.meal_items || []).filter(item => (item as any).id);
      const itemsToCreate = (formValues.meal_items || []).filter(item => !(item as any).id);

      // 3. Update existing meal items
      const updatePromises = itemsToUpdate.map(async (item: UpdateMealItemRequest) => {
        await mealApi.appApiMealPutMealItem({
          patient_id: patient.patient_id,
          meal_id: selectedMeal.id,
          meal_item_id: item.id,
          UpdateMealItemRequest: {
            ...item,
            addons: item.addons ?? [],
            extra_addons: item.extra_addons ?? [],
          },
        });
      });

      // 4. Create new meal items (if any)
      if (itemsToCreate.length > 0) {
        await mealApi.appApiMealPostMealItems({
          patient_id: patient.patient_id,
          meal_id: selectedMeal.id,
          CreateMealItemRequest: itemsToCreate.map(item => ({
            food_name: item.food_name,
            servings: item.servings,
            serving_unit_label: item.serving_unit_label,
            serving_unit_amount: item.serving_unit_amount,
            addons: item.addons ?? [],
            extra_addons: item.extra_addons ?? [],
            custom_addons: item.custom_addons ?? [],
            note: item.note,
          })),
        });
      }

      await Promise.all(updatePromises);

      // Invalidate the specific query instead of all queries
      await queryClient.invalidateQueries({
        queryKey: ['meal-timeline', patient.patient_id],
        refetchType: 'active',
      });

      onHide();
    } catch (error) {
      console.error('Error updating meal or meal items:', error);
      Sentry.captureException(error);
      alert(t('Error updating meal or meal items. Please try again.'));
    }
  };

  const handleSave = async () => {
    if (isSaving) {
      return;
    } // Prevent multiple calls

    setIsSaving(true);
    try {
      if (displayMode === DISPLAY_MODE_EDIT && selectedMeal && !isCopyMode) {
        await handleUpdateMeal();
      } else {
        await handleSaveMeal();
      }
    } finally {
      setIsSaving(false);
    }
  };

  const handleSaveMeal = async () => {
    try {
      const formValues = form.getValues();

      // Remove id fields from meal items and custom_addons for POST
      const mealRequest: CreateMealRequest = {
        meal_date: formValues.meal_date,
        meal_time: formValues.meal_time,
        meal_name: formValues.meal_name.toLowerCase() as CreateMealRequestMealNameEnum,
        meal_items: formValues.meal_items?.map(item => ({
          // Do NOT include id here!
          food_name: item.food_name,
          servings: item.servings,
          serving_unit_label: item.serving_unit_label,
          serving_unit_amount: item.serving_unit_amount,
          addons: item.addons ?? [],
          extra_addons: item.extra_addons ?? [],
          custom_addons: item.custom_addons?.map(addon => ({
            // Do NOT include id here!
            food_name: addon.food_name,
            servings: addon.servings,
            serving_unit_label: addon.serving_unit_label,
            serving_unit_amount: addon.serving_unit_amount,
          })) || [],
          note: item.note || '',
        })) || [],
      };

      const response = await mealApi.appApiMealPostMeals({
        patient_id: patient.patient_id,
        create_queue: true,
        CreateMealRequest: mealRequest,
      });

      if (response.data) {
        // Use the new cache structure
        globalUseMealCache.getState().addMeal(response.data);

        // Invalidate the specific query instead of all queries
        await queryClient.invalidateQueries({
          queryKey: ['meal-timeline', patient.patient_id],
          refetchType: 'active',
        });

        // Close the modal
        onHide();
      }
    } catch (error) {
      console.error('Error saving meal:', error);
      Sentry.captureException(error);
      alert(t('Error saving meal. Please try again.'));
    }
  };

  const handleDeleteMeal = async () => {
    const conf = confirm(t('Are you sure you want to permanently delete this meal?'));

    if (!conf) {
      alert(t('Delete cancelled'));
      return true;
    }

    const res = _handleDeleteMeal();
    deleteMealReq.bind(res);
    const [response, err] = await maybe(res);

    if (err) {
      alert(`${t('Error deleting meal')}: ${err}`);
      return;
    }
    track(ANALYTICS_EVENTS.MEAL_DELETED, {
      'Patient ID': patient.patient_id,
      'Meal ID': selectedMeal?.id,
    });

    // Use the new cache structure
    globalUseMealCache.getState().updateMeal(selectedMeal.id.toString(), {
      is_deleted: true,
    });

    onHide();
    // eslint-disable-next-line i18next/no-literal-string
    setDisplayMode(DISPLAY_MODE_VIEW);
  };

  const _handleDeleteMeal = async () => {
    const res = await mealApi.appApiMealDeleteMeal({
      patient_id: patient.patient_id,
      meal_id: selectedMeal.id,
    });
    queryClient.resetQueries({ queryKey: [MACRO_NUTRIENT_QUERY_KEY] });
    return res;
  };

  const MacroSummaryCard = React.useMemo(() => {
    if (!selectedMeal || editing) {
      return null;
    }
    const date = DateTime.fromISO(selectedMeal.meal_date);
    return (
      <div style={{ width: 460 }}>
        <MacroNutrientSummaryCard summaryDate={date} mealId={selectedMeal.id} meal={selectedMeal} />
      </div>
    );
  }, [selectedMeal, editing]);

  return (
    <FormProvider {...form}>
      <Modal show={true} onHide={handleHide} className="mealview-modal">
        <Modal.Body onClick={(e) => isCopyMode && e.stopPropagation()}>
          {!editing && !isCopyMode && selectedMeal?.meal_photo_id && !mealPhotoShowLarge && (
            <Row>
              <Col xs="auto">
                <OriginalImage
                  mealPhotoId={selectedMeal?.meal_photo_id}
                  style={{
                    width: 250,
                    height: 250,
                    objectFit: 'cover',
                    cursor: 'pointer',
                  }}
                  onClick={(e) => {
                    e.stopPropagation();
                    setMealPhotoShowLarge(true);
                  }}
                />
              </Col>
            </Row>
          )}

          {!editing && !isCopyMode && selectedMeal?.meal_photo_id && mealPhotoShowLarge && (
            <Row>
              <Col xs="auto" style={{ position: 'relative' }}>
                <div style={{ position: 'absolute', top: 10, right: -10, zIndex: 1000 }}>
                  <span
                    className="close-btn"
                    onClick={e => {
                      e.stopPropagation();
                      setMealPhotoShowLarge(false);
                    }}
                    style={{ cursor: 'pointer', fontSize: '24px', color: '#626262' }}
                  >
                    <i className="fas fa-times" />
                  </span>
                </div>
                <OriginalImage
                  mealPhotoId={selectedMeal?.meal_photo_id}
                  style={{
                    width: '100%',
                    maxWidth: '600px',
                    height: 'auto',
                    objectFit: 'contain',
                    cursor: 'pointer',
                  }}
                  onClick={e => {
                    e.stopPropagation();
                    setMealPhotoShowLarge(false);
                  }}
                />
              </Col>
            </Row>
          )}

          {!editing && !isCopyMode
            ? (
              <>
                <Row className="popup-header">
                  <Col xs="auto">
                    <div className="d-flex">
                      <h5 className="text-muted">{formatDateLongMonth(i18n.language, selectedMeal?.meal_date)}</h5>
                      <div className="mx-2 text-muted">
                        <Trans>•</Trans>
                      </div>
                      <div className="text-muted">{FormatAMPMStr(selectedMeal?.meal_time)}</div>
                      {clinician.is_internal && (
                        <div style={{ marginLeft: '20px' }}>
                          <Trans>Meal ID:</Trans>
                          {selectedMeal?.id}
                        </div>
                      )}
                    </div>
                    <h4>{titleCase(t(selectedMeal?.meal_name))}</h4>
                  </Col>
                  <Col xs="auto" className="ml-auto">
                    {clinician.flags.clinician_allow_meal_edit && (
                      <span
                        className="edit-btn mx-3"
                        onClick={() => setDisplayMode(DISPLAY_MODE_EDIT)}
                      >
                        <i className="fas fa-pen" />
                      </span>
                    )}
                    <span className="trash-btn mx-3">
                      <i
                        className="fas fa-trash"
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          if (!deleteMealReq.isPending) {
                            handleDeleteMeal();
                          }
                        }}
                        style={{
                          cursor: deleteMealReq.isPending ? 'not-allowed' : 'pointer',
                          opacity: deleteMealReq.isPending ? 0.6 : 1,
                        }}
                      />
                    </span>
                    <span className="close-btn mx-3">
                      <i className="fas fa-times" onClick={handleHide} />
                    </span>
                  </Col>
                </Row>
                <div className="meals-container mt-4">
                  <div className="mb-2">
                    <Trans count={selectedMeal?.meal_items.length}>
                      {{ count: selectedMeal?.meal_items.length }} <Trans>foods logged</Trans>
                    </Trans>
                  </div>
                  {selectedMeal?.meal_items.map((mealItem, idx) => {
                    const addonsStr = itemAddonsToStr(tAttr, mealItem);
                    return (
                      <Row key={mealItem.id || (mealItem as any)._rhf_id} className="mx-0">
                        <Col className="py-3 bx-1 d-flex justify-content-between" style={{ gap: '20px' }}>
                          <img src={mealItem.food_photo_url || MealThumbnailImage} width={80} height={80} />
                          <div className="flex-1">
                            <h5 className="mb-0">{titleCase(tAttr(mealItem, 'food_name'))}</h5>
                            <div>
                              <MealItemPercentEaten item={mealItem} />
                              {mealItem.servings} {tAttr(mealItem, 'serving_unit_label')}
                              {addonsStr && (' ' + t(`with`) + ' ' + addonsStr)}
                            </div>
                            {!!mealItem.note && <div>{t('Notes:') + ' ' + mealItem.note}</div>}
                          </div>
                          <MealItemMacros mealItem={mealItem} />
                        </Col>
                      </Row>
                    );
                  })}
                </div>

                {MacroSummaryCard}

                {!editing && patientFlags.flags.patient_show_dexcom_oauth && (
                  <div>
                    <div className="mt-3">
                      <Trans>Glucose Profile (4hr after meal)</Trans>
                    </div>
                    <div style={{ width: '50%' }}>
                      {selectedMeal && (
                        <CGMMealGlucoseChart
                          patient_id={patient.patient_id}
                          meal_date={selectedMeal.meal_date}
                          meal_time={selectedMeal.meal_time}
                          activityData={activityData}
                        />
                      )}
                    </div>
                  </div>
                )}
                {selectedMeal && (
                  <div className="mt-3">
                    <PatientSurvey mealId={selectedMeal.id} />
                  </div>
                )}
              </>
            )
            : (
              <>
                <Row className="popup-header">
                  <Col xs="auto">
                    {isCopyMode && (
                      <>
                        <h4 className="mb-3">
                          <Trans>Copy to a new meal</Trans>
                        </h4>
                        <p className="text-muted mb-3" style={{ fontSize: '0.7rem' }}>
                          <Trans>
                            Enter the new date and time, adjust the food items below, and click the check mark to create
                            a new meal
                          </Trans>
                        </p>
                      </>
                    )}
                    <div className="d-flex" style={{ gap: '20px' }}>
                      <Controller
                        name="meal_date"
                        control={form.control}
                        render={({ field: { onChange, value }, fieldState: { error } }) => (
                          <MuiPickersUtilsProvider
                            utils={DateFnsUtils}
                            locale={i18n.language}
                          >
                            <DatePicker
                              className="datepicker"
                              inputVariant="outlined"
                              disableToolbar
                              format="YYYY-MM-DD"
                              label={t('Meal Date')}
                              variant="inline"
                              value={value ? DateTime.fromISO(value).toJSDate() : null}
                              onChange={(date) => {
                                if (date) {
                                  const formattedDate = moment(date).format('YYYY-MM-DD');
                                  onChange(formattedDate);
                                }
                              }}
                              onClick={(e) => e.stopPropagation()}
                              autoOk
                            />
                          </MuiPickersUtilsProvider>
                        )}
                      />
                      <Controller
                        name="meal_time"
                        control={form.control}
                        render={({ field: { onChange, value }, fieldState: { error } }) => (
                          <MuiPickersUtilsProvider
                            utils={DateFnsUtils}
                            locale={i18n.language}
                          >
                            <TimePicker
                              className="datepicker"
                              inputVariant="outlined"
                              format="hh:mm A"
                              label={t('Meal time')}
                              variant="inline"
                              value={value ? DateTime.fromISO(value).toJSDate() : null}
                              onChange={(date) => {
                                if (date) {
                                  const formattedTime = moment(date).format('HH:mm:ss');
                                  onChange(formattedTime);
                                }
                              }}
                              onClick={(e) => e.stopPropagation()}
                              autoOk
                            />
                          </MuiPickersUtilsProvider>
                        )}
                      />
                    </div>
                    <select
                      className="w-30 form-control mt-3"
                      defaultValue={selectedMeal?.meal_name}
                      style={{ width: '220px', height: '40px' }}
                      {...form.register('meal_name')}
                      onClick={(e) => e.stopPropagation()}
                      onChange={(e) => {
                        e.stopPropagation();
                        form.setValue('meal_name', e.target.value as CreateMealRequestMealNameEnum);
                      }}
                    >
                      <option value="breakfast">{t('Breakfast')}</option>
                      <option value="lunch">{t('Lunch')}</option>
                      <option value="dinner">{t('Dinner')}</option>
                      <option value="snack">{t('Snack')}</option>
                    </select>
                  </Col>
                  <Col xs="auto" className="ml-auto">
                    <span
                      className="edit-btn mx-3"
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        if (!isSaving && !saveMealReq.isPending) {
                          form.handleSubmit(handleSave)();
                        }
                      }}
                      style={{
                        cursor: (isSaving || saveMealReq.isPending) ? 'not-allowed' : 'pointer',
                        opacity: (isSaving || saveMealReq.isPending) ? 0.6 : 1,
                        pointerEvents: (isSaving || saveMealReq.isPending) ? 'none' : 'auto',
                      }}
                    >
                      {!isSaving && !saveMealReq.isPending && (
                        <i
                          className="fas fa-check"
                          onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            if (!isSaving && !saveMealReq.isPending) {
                              form.handleSubmit(handleSave)();
                            }
                          }}
                        />
                      )}
                      {(isSaving || saveMealReq.isPending) && <i className="fas fa-spinner fa-spin" />}
                    </span>
                    <span className="close-btn mx-3" onClick={handleHide}>
                      <i className="fas fa-times" />
                    </span>
                  </Col>
                </Row>

                <div className="meals-container mt-4">
                  {form.watch('meal_items')?.map((mealItem, index) => (
                    <EditMealItemRow
                      key={index}
                      mealItem={mealItem}
                      onChange={(fields) => {
                        const currentItems = form.getValues('meal_items');
                        currentItems[index] = { ...currentItems[index], ...fields };
                        form.setValue('meal_items', currentItems);
                      }}
                      mealIndex={index}
                      register={(name, options) => form.register(`meal_items.${index}.${name}`, options)}
                      onRemove={() => {
                        const currentItems = form.getValues('meal_items');
                        form.setValue('meal_items', currentItems.filter((_, i) => i !== index));
                      }}
                    />
                  ))}

                  <Row className="mx-0" style={{ borderTop: '1px solid grey' }}>
                    <Col className="py-3" style={{ paddingLeft: 0 }}>
                      <h5 className="mt-2">
                        <span
                          className="addMealBtn"
                          onClick={() => {
                            const currentItems = form.getValues('meal_items') || [];
                            form.setValue('meal_items', [
                              ...currentItems,
                              {
                                food_name: '',
                                servings: 1,
                                serving_unit_label: '',
                                serving_unit_amount: 0,
                                addons: [],
                                extra_addons: [],
                                custom_addons: [],
                                note: '',
                              },
                            ]);
                          }}
                        >
                          <Trans>Add food</Trans> <i className="fas fa-plus mr-2" />
                        </span>
                      </h5>
                    </Col>
                  </Row>
                </div>
              </>
            )}
        </Modal.Body>
      </Modal>
    </FormProvider>
  );
};
